<template>
  <div class="bsm-root">

    <v-menu
      v-model="open"
      offset-y
      :close-on-content-click="false"
      transition="bsm-panel"
    >
      <template v-slot:activator="{ on, attrs }">
        <v-btn v-bind="attrs" v-on="on" class="bsm-btn" icon small :title="open ? 'Cerrar' : 'Resumen rápido'">
          <v-icon color="white" size="14">mdi-robot-outline</v-icon>
        </v-btn>
      </template>

      <v-card class="bsm-panel" role="dialog" aria-label="Resumen rápido" @click.stop>

        <div v-if="showHeader" class="bsm-header">
          <div class="bsm-header__icon"><v-icon size="14" color="#1C3A7A">mdi-robot-outline</v-icon></div>
          <div class="bsm-header__text">
            <span class="bsm-header__title">Resumen</span>
            <span class="bsm-header__sub" v-if="contextLabel">{{ contextLabel }}</span>
          </div>
        </div>

        <div class="bsm-suggestions">
          <div class="bsm-suggestions__label">Sugerencias</div>
          <div class="bsm-chips">
            <button
              v-for="chip in suggestionChips"
              :key="chip"
              class="bsm-chip"
              :class="{ 'bsm-chip--active': chip === activeChip, 'bsm-chip--done': !!cache[chip] }"
              :disabled="isLoading || !currentElement"
              :title="chip"
              @click="sendSummaryChip(chip)"
            >
              <span class="bsm-chip__dot" v-if="cache[chip]"></span>
              {{ chip }}
            </button>
          </div>
        </div>

        <div class="bsm-content">
          <transition name="bsm-fade" mode="out-in">

            <div v-if="isLoading" key="loading" class="bsm-loading">
              <div class="bsm-loading__status">
                <span class="bsm-loading__spinner"></span>
                <span class="bsm-loading__text">{{ loadingMessages[loadingMessageIndex] }}</span>
              </div>
              <div class="bsm-skeleton">
                <div class="bsm-skeleton__line" style="width: 92%"></div>
                <div class="bsm-skeleton__line" style="width: 78%"></div>
                <div class="bsm-skeleton__line" style="width: 85%"></div>
                <div class="bsm-skeleton__line" style="width: 60%"></div>
              </div>
            </div>

            <div v-else-if="summary" key="summary" class="bsm-result">
              <div class="bsm-result__label">
                <v-icon size="12" color="#1C3A7A">mdi-text-box-outline</v-icon>
                {{ activeChip }}
              </div>
              <div class="bsm-result__content" v-html="summary"></div>
            </div>

            <div v-else-if="!currentElement" key="empty" class="bsm-empty">
              <v-icon size="18" color="#B7C1D9">mdi-file-search-outline</v-icon>
              <span>Abre un elemento para ver opciones de resumen.</span>
            </div>

            <div v-else key="hint" class="bsm-empty">
              <v-icon size="18" color="#B7C1D9">mdi-cursor-default-click-outline</v-icon>
              <span>Elige una sugerencia para comenzar.</span>
            </div>

          </transition>
        </div>

      </v-card>
    </v-menu>

  </div>
</template>

<script>
import { chatWithTools } from "@/helpers/api/bot"

const TYPE_LABELS = {
  task: 'Tarea', tarea: 'Tarea',
  rdi: 'RDI',
  entrega: 'Entrega',
	adicional: 'Adicional'
}

export default {
  name: 'BotSummarizeMini',

  props: {
    showHeader: { type: Boolean, default: false },
  },

  data: () => ({
    open: false,
    isLoading: false,
    summary: '',
    activeChip: null,
    cache: {},
    contextObject: null,
    trackedElementKey: null,
    loadingMessageIndex: 0,
    loadingTimer: null,
    loadingMessages: [
      'Analizando el elemento...',
      'Recopilando información relacionada...',
      'Consultando el contexto del proyecto...',
      'Redactando el resumen...',
      'Casi listo...',
    ],
  }),

  computed: {
		currentTarea()    {
			if(!this.$store.consultasStore.state.currentTarea) return null
			console.log('currentTarea', this.$store.consultasStore.state.currentTarea)
			return {...this.$store.consultasStore.state.currentTarea, ...{element_type: 'tarea'}}
		},
		currentRDI()      {
			if(!this.$store.rdiStore.state.currentRDI) return null
			return {...this.$store.rdiStore.state.currentRDI, ...{element_type: 'rdi'}}
		},
		currentAdicional(){
			if(!this.$store.adicionalesStore.state.currentAdicional) return null
			return {...this.$store.adicionalesStore.state.currentAdicional, ...{element_type: 'adicional'}}
		},
		currentEntrega()  {
			if(!this.$store.entregaStore.state.currentEntrega) return null
			return {...this.$store.entregaStore.state.currentEntrega, ...{element_type: 'entrega'}}
		},

    currentElement() {
      return this.currentTarea || this.currentRDI || this.currentAdicional || this.currentEntrega || null
    },

    selectedProject() {
      return this.$store.projectsStore.state.selectedProject
    },

    contextLabel() {
      const el = this.currentElement
      if (!el) return null
      return el.title || el.name || el.code || el.label || null
    },

		elementTypeName() {
			const el = this.currentElement
			if (!el) return 'elemento'
			return TYPE_LABELS[(el.element_type || '').toLowerCase()] || el.element_type || 'elemento'
		},

    projectId() {
      if (this.currentElement && this.currentElement.project_id) {
        return this.currentElement.project_id
      }
      return this.selectedProject ? this.selectedProject.id : null
    },

    elementKey() {
      const el = this.currentElement
      if (!el) return null
      return `${el.type || ''}:${el.id || el.code || ''}`
    },

		suggestionChips() {
			if (!this.currentElement) return []
			const labelPart = this.contextLabel ? ` ${this.contextLabel}` : (this.currentElement && this.currentElement.id ? ` #${this.currentElement.id}` : '')
			return [
				`Resumir ${this.elementTypeName}: ${labelPart}`
			]
		},
  },

  watch: {
    elementKey(newKey) {
      if (newKey !== this.trackedElementKey) {
        this.trackedElementKey = newKey
        this.cache = {}
        this.activeChip = null
        this.summary = ''
        this.contextObject = null
      }
    },
		suggestionChips: {
			handler(chips) {
				if(chips.length == 1 && !this.activeChip && this.open) {
					this.sendSummaryChip(chips[0])
				}
			},
			immediate: true
		},
		open(newVal) {
			if(newVal && this.suggestionChips.length == 1 && !this.activeChip) {
				this.sendSummaryChip(this.suggestionChips[0])
			}
		}
  },

  beforeDestroy() {
    clearInterval(this.loadingTimer)
  },

  methods: {
    toggleOpen() {
      this.open = !this.open
    },

    async sendSummaryChip(chip) {
      if (!this.currentElement || this.isLoading) return

      this.activeChip = chip

      // Ya generado antes: mostrar el contenido previo, sin volver a pedirlo.
      if (this.cache[chip]) {
        this.summary = this.cache[chip]
        return
      }

      this.isLoading = true
      this.summary = ''
      this.startLoadingMessages()

      try {
        const payload = {
          project_id: this.projectId,
          messages: [{ role: 'user', content: chip }],
          context: this.buildContext(),
        }

        const response = await chatWithTools(payload)
        if (!response || !response.success) {
          this.summary = '⚠️ No pude obtener el resumen. Intenta nuevamente seleccionando una sugerencia.'
          return
        }

        const data = response.data || {}
        if (data.loaded_element) {
          this.contextObject = data.loaded_element
        }

        const content = data.content || data.message || data.text || 'No se recibió contenido.'
        this.cache = { ...this.cache, [chip]: content }
        this.summary = content
      } catch (err) {
        this.summary = '⚠️ No pude conectarme con el asistente. Intenta nuevamente seleccionando una sugerencia.'
      } finally {
        this.isLoading = false
        this.stopLoadingMessages()
      }
    },

    startLoadingMessages() {
      this.loadingMessageIndex = 0
      clearInterval(this.loadingTimer)
      this.loadingTimer = setInterval(() => {
        if (this.loadingMessageIndex < this.loadingMessages.length - 1) {
          this.loadingMessageIndex++
        }
      }, 2500)
    },

    stopLoadingMessages() {
      clearInterval(this.loadingTimer)
      this.loadingTimer = null
    },

    buildContext() {
      const ctx = {}
      if (this.contextObject) {
        ctx.context_object = this.contextObject
      } else if (this.currentElement) {
        ctx.context_object = this.currentElement
      }
      if (this.contextLabel) {
        ctx.context_label = this.contextLabel
      }
      return Object.keys(ctx).length ? ctx : undefined
    },
  },
}
</script>

<style scoped>
.bsm-root { position: relative; display: inline-block; }
.bsm-btn {
  width: 28px; height: 28px; border-radius: 8px; border: none;
  background: linear-gradient(135deg, #22448F 0%, #1C3A7A 100%); color: white;
  display:flex; align-items:center; justify-content:center;
  cursor: pointer; box-shadow: 0 6px 14px rgba(28,58,122,.28);
}

.bsm-panel {
  width: 550px; margin-top: 8px; padding: 12px;
  background: #fff; border: 1px solid #E6EDF8; border-radius: 14px;
  box-shadow: 0 16px 40px rgba(20,30,60,.12);
}

/* Header */
.bsm-header { display:flex; align-items:center; gap:10px; margin-bottom:10px; padding-bottom:10px; border-bottom: 1px solid #EEF2FB; }
.bsm-header__icon {
  width: 26px; height: 26px; border-radius: 8px; background: #EEF2FB;
  display:flex; align-items:center; justify-content:center; flex-shrink: 0;
}
.bsm-header__text { display:flex; flex-direction:column; min-width:0; }
.bsm-header__title { font-weight:700; color:#1C3A7A; font-size:13px; line-height:1.2; }
.bsm-header__sub { font-size:11.5px; color:#8b96b0; white-space:nowrap; overflow:hidden; text-overflow:ellipsis; }

/* Suggestions section */
.bsm-suggestions {
  background: #F5F7FC; border: 1px solid #E6EDF8; border-radius: 10px;
  padding: 8px 9px; margin-bottom: 10px;
}
.bsm-suggestions__label {
  font-size: 10px; font-weight: 700; text-transform: uppercase; letter-spacing: .04em;
  color: #97A2BE; margin-bottom: 7px; padding-left: 1px;
}

/* Chips - compact */
.bsm-chips { display:flex; gap:5px; flex-wrap:wrap; }
.bsm-chip {
  position: relative;
  font-size:11px; font-weight: 500; color:#2D4B95; background:#fff;
  border:1px solid #C5D0EA; padding:4px 9px; border-radius:14px; cursor:pointer;
  display:flex; align-items:center; gap:4px; line-height:1.3;
  max-width: 100%; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;
  transition: background .15s ease, border-color .15s ease, transform .1s ease, color .15s ease;
}
.bsm-chip:hover:not(:disabled) { background:#E1E9FA; border-color:#AFC0E6; }
.bsm-chip:active:not(:disabled) { transform: scale(.97); }
.bsm-chip:disabled { opacity:.45; cursor:not-allowed; }
.bsm-chip--active {
  background: #1C3A7A; border-color:#1C3A7A; color:#fff;
  box-shadow: 0 3px 8px rgba(28,58,122,.25);
}
.bsm-chip--done:not(.bsm-chip--active) { border-color:#8FB08A; }
.bsm-chip__dot {
  width: 5px; height: 5px; border-radius: 50%; background:#4CAF50; flex-shrink:0;
}
.bsm-chip--active .bsm-chip__dot { background:#9BE39B; }

/* Content section wrapper */
.bsm-content { width: 100%; }

/* Loading */
.bsm-loading { display:flex; flex-direction:column; gap:9px; }
.bsm-loading__status {
  display:flex; align-items:center; gap:8px;
  padding: 8px 10px; background:#F5F7FC; border: 1px solid #E6EDF8; border-radius: 8px;
}
.bsm-loading__spinner {
  width: 13px; height: 13px; border-radius: 50%; flex-shrink: 0;
  border: 2px solid #C5D0EA; border-top-color: #1C3A7A;
  animation: bsm-spin .7s linear infinite;
}
@keyframes bsm-spin { to { transform: rotate(360deg); } }
.bsm-loading__text {
  font-size: 12px; color: #4A5578; font-weight: 500;
}

/* Skeleton loading */
.bsm-skeleton {
  padding: 12px; display:flex; flex-direction:column; gap:9px;
  border: 1px solid #EEF2FB; border-radius: 10px; background:#FBFCFE;
}
.bsm-skeleton__line {
  height: 10px; border-radius: 6px;
  background: linear-gradient(90deg, #EEF2FB 25%, #E1E8F7 37%, #EEF2FB 63%);
  background-size: 400% 100%;
  animation: bsm-shimmer 1.3s ease-in-out infinite;
}
@keyframes bsm-shimmer {
  0% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
}

/* Result */
.bsm-result {
  border-radius: 10px; background: #fff; border: 1px solid #E6EDF8;
  overflow: hidden;
}
.bsm-result__label {
  display:flex; align-items:center; gap:5px;
  font-size: 10.5px; font-weight: 700; text-transform: uppercase; letter-spacing: .03em;
  color: #7C88A8; padding: 9px 12px; background:#F5F7FC; border-bottom: 1px solid #EEF2FB;
  white-space:nowrap; overflow:hidden; text-overflow:ellipsis;
}
.bsm-result__content {
  font-size:13px; color:#293150; line-height:1.6; word-break: break-word;
  padding: 11px 12px; max-height: 350px; overflow-y: auto;
}
.bsm-result__content >>> strong { color: #1C3A7A; }
.bsm-result__content >>> code {
  background:#EEF2FB; padding:1px 5px; border-radius:4px; font-size:12px;
}

/* Empty / hint states */
.bsm-empty {
  font-size:12px; color:#8b96b0; padding: 22px 4px; text-align:center;
  display:flex; flex-direction:column; align-items:center; gap:6px;
  border: 1px dashed #E6EDF8; border-radius: 10px;
}

/* Fade transition between states */
.bsm-fade-enter-active, .bsm-fade-leave-active { transition: opacity .15s ease; }
.bsm-fade-enter, .bsm-fade-leave-to { opacity: 0; }

/* Panel transition */
.bsm-panel-enter-active, .bsm-panel-leave-active { transition: all .18s ease }
.bsm-panel-enter { transform: translateY(-6px); opacity: 0 }
.bsm-panel-enter-to { transform: translateY(0); opacity: 1 }
.bsm-panel-leave { transform: translateY(0); opacity: 1 }
.bsm-panel-leave-to { transform: translateY(-6px); opacity: 0 }
</style>